class ReyMenu extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-menu-toggle]');
    this.panel = this.querySelector('[data-menu-panel]');
    this.closeButton = this.querySelector('[data-menu-close]');
    if (!this.button || !this.panel) return;
    this.button.addEventListener('click', () => this.toggle());
    this.closeButton?.addEventListener('click', () => this.close());
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 32);
    this.onScroll();
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    this.releaseMenuInert();
  }

  toggle() {
    const isOpen = this.button.getAttribute('aria-expanded') === 'true';
    this.button.setAttribute('aria-expanded', String(!isOpen));
    this.panel.hidden = isOpen;
    document.body.classList.toggle('menu-open', !isOpen);
    this.classList.toggle('is-menu-open', !isOpen);
    if (!isOpen) {
      this.menuInerted = [...document.body.children].filter((element) => !element.contains(this) && element.tagName !== 'SCRIPT');
      this.menuInerted.forEach((element) => { element.inert = true; });
      const firstTarget = this.panel.querySelector('[data-menu-close], a, button');
      requestAnimationFrame(() => firstTarget?.focus());
    } else {
      this.releaseMenuInert();
    }
  }

  close() {
    this.button.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
    document.body.classList.remove('menu-open');
    this.classList.remove('is-menu-open');
    this.releaseMenuInert();
    this.button.focus();
  }

  releaseMenuInert() {
    this.menuInerted?.forEach((element) => { element.inert = false; });
    this.menuInerted = [];
  }

  onKeydown(event) {
    if (this.panel.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

class AgeGate extends HTMLElement {
  connectedCallback() {
    let verified = false;
    try { verified = localStorage.getItem('rey-age-verified') === 'true'; } catch (error) { /* Storage can be blocked. */ }
    if (verified) {
      window.__reyAgeVerified = true;
      document.documentElement.classList.add('age-verified');
      this.hidden = true;
      window.dispatchEvent(new CustomEvent('age:verified'));
      return;
    }
    this.returnFocus = document.activeElement;
    this.hidden = false;
    document.body.classList.add('age-gate-open');
    this.inerted = [...document.body.children].filter((element) => !element.contains(this) && element.tagName !== 'SCRIPT');
    this.inerted.forEach((element) => { element.inert = true; });
    this.querySelector('[data-age-confirm]')?.addEventListener('click', () => this.confirm());
    this.querySelector('[data-age-decline]')?.addEventListener('click', () => this.decline());
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.querySelector('[data-age-confirm]')?.focus();
  }

  confirm() {
    try { localStorage.setItem('rey-age-verified', 'true'); } catch (error) { /* Continue for this visit. */ }
    window.__reyAgeVerified = true;
    document.documentElement.classList.add('age-verified');
    this.hidden = true;
    document.body.classList.remove('age-gate-open');
    this.inerted?.forEach((element) => { element.inert = false; });
    window.dispatchEvent(new CustomEvent('age:verified'));
    this.returnFocus?.focus?.();
  }

  decline() {
    const message = this.querySelector('[data-age-message]');
    if (message) message.textContent = 'You must be of legal drinking age to enter this site.';
  }

  onKeydown(event) {
    if (event.key !== 'Tab') return;
    const focusable = [...this.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.panels = [...this.querySelectorAll('[data-gallery-panel]')];
    this.currentOutput = this.querySelector('[data-gallery-current]');
    this.previousButton = this.querySelector('[data-gallery-previous]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    const visiblePanel = this.panels.find((panel) => !panel.hidden) || this.panels[0];
    this.index = Math.max(0, this.panels.indexOf(visiblePanel));

    this.buttons.forEach((button, index) => {
      button.addEventListener('click', () => this.selectByIndex(index));
      button.addEventListener('keydown', (event) => this.onThumbKeydown(event, index));
    });
    this.previousButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));
    this.render();
  }

  selectById(id) {
    const index = this.panels.findIndex((panel) => String(panel.dataset.galleryPanel) === String(id));
    if (index >= 0) this.selectByIndex(index);
  }

  selectByIndex(index, focusThumb = false) {
    if (!this.panels.length) return;
    this.index = (index + this.panels.length) % this.panels.length;
    this.render();
    if (focusThumb) this.buttons[this.index]?.focus();
  }

  step(direction) {
    this.selectByIndex(this.index + direction);
  }

  render() {
    this.panels.forEach((panel, index) => {
      const selected = index === this.index;
      panel.hidden = !selected;
      panel.setAttribute('aria-hidden', String(!selected));
      if (!selected) panel.querySelectorAll('video, audio').forEach((media) => media.pause?.());
    });
    this.buttons.forEach((button, index) => {
      const selected = index === this.index;
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
    });
    if (this.currentOutput) this.currentOutput.textContent = String(this.index + 1);
  }

  onThumbKeydown(event, index) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = index + 1;
    if (event.key === 'ArrowLeft') nextIndex = index - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.buttons.length - 1;
    this.selectByIndex(nextIndex, true);
  }
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.root = this.closest('[data-product-root]') || this;
    this.status = this.querySelector('[data-product-status]');
    this.variantInput = this.querySelector('[data-variant-id]');
    if (this.variantInput) this.variantInput.name = 'id';
    this.optionSelectors = [...this.querySelectorAll('[data-option-index]')];
    this.quantityInput = this.querySelector('input[name="quantity"]');
    this.submitButtons = [...this.root.querySelectorAll('[data-product-submit]')];
    this.priceOutputs = [...this.root.querySelectorAll('[data-product-price]')];
    this.comparePriceOutputs = [...this.root.querySelectorAll('[data-product-compare-price]')];
    this.submitPriceOutputs = [...this.root.querySelectorAll('[data-product-submit-price]')];
    this.availabilityOutput = this.root.querySelector('[data-product-availability]');

    try {
      this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
    } catch (error) {
      this.variants = [];
    }

    this.form.addEventListener('submit', (event) => this.submit(event));
    this.optionSelectors.forEach((select) => select.addEventListener('change', () => this.onOptionsChange()));
    this.querySelectorAll('[data-quantity-adjust]').forEach((button) => {
      button.addEventListener('click', () => this.adjustQuantity(Number(button.dataset.quantityAdjust)));
    });

    const initialVariant = this.variants.find((variant) => String(variant.id) === String(this.variantInput?.value));
    if (initialVariant) this.applyVariant(initialVariant, false);
    this.setupMobileBar();
  }

  disconnectedCallback() {
    this.mobileBarObserver?.disconnect();
    this.footerObserver?.disconnect();
  }

  setupMobileBar() {
    this.mobileBar = this.root.querySelector('[data-mobile-atc]');
    this.primarySubmit = this.form.querySelector('[data-product-submit]');
    if (!this.mobileBar || !this.primarySubmit) return;

    this.primarySubmitVisible = true;
    this.footerVisible = false;
    const update = () => this.mobileBar.classList.toggle('is-visible', !this.primarySubmitVisible && !this.footerVisible);

    if (!('IntersectionObserver' in window)) {
      this.mobileBar.classList.add('is-visible');
      return;
    }

    this.mobileBarObserver = new IntersectionObserver(([entry]) => {
      // Keep the bar quiet before the customer reaches the primary form;
      // reveal it only after that purchase control has passed above the viewport.
      this.primarySubmitVisible = entry.isIntersecting || entry.boundingClientRect.top >= 0;
      update();
    }, { threshold: 0.01 });
    this.mobileBarObserver.observe(this.primarySubmit);

    const footer = document.querySelector('footer, .site-footer');
    if (footer) {
      this.footerObserver = new IntersectionObserver(([entry]) => {
        this.footerVisible = entry.isIntersecting;
        update();
      }, { threshold: 0.01 });
      this.footerObserver.observe(footer);
    }
  }

  onOptionsChange() {
    const selectedOptions = this.optionSelectors.map((select) => select.value);
    const variant = this.variants.find((item) => item.options.every((option, index) => option === selectedOptions[index]));
    this.applyVariant(variant, true);
  }

  applyVariant(variant, updateUrl) {
    this.currentVariant = variant;
    const available = Boolean(variant?.available);
    const label = variant ? (available ? 'Add to bag' : 'Sold out') : 'Unavailable';

    if (this.variantInput) {
      this.variantInput.value = variant?.id || '';
      this.variantInput.disabled = !variant;
    }

    this.submitButtons.forEach((button) => {
      button.disabled = !available;
      button.querySelector('[data-product-submit-label]')?.replaceChildren(label);
    });

    if (variant) {
      this.priceOutputs.forEach((output) => { output.textContent = variant.price; });
      this.submitPriceOutputs.forEach((output) => {
        output.textContent = output.classList.contains('visually-hidden') ? ` for ${variant.price}` : `· ${variant.price}`;
        output.hidden = !available;
      });
      this.comparePriceOutputs.forEach((output) => {
        output.textContent = variant.compareAtPrice || '';
        output.hidden = !variant.onSale;
      });
    }

    if (this.availabilityOutput) {
      this.availabilityOutput.textContent = variant ? (available ? 'Available' : 'Sold out') : 'Unavailable';
      this.availabilityOutput.classList.toggle('is-unavailable', !available);
    }

    if (variant?.featuredMediaId) this.root.querySelector('product-gallery')?.selectById(variant.featuredMediaId);

    if (variant && updateUrl && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    this.status.textContent = '';
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
  }

  adjustQuantity(change) {
    if (!this.quantityInput) return;
    const minimum = Number(this.quantityInput.min) || 1;
    const maximum = Number(this.quantityInput.max) || Number.MAX_SAFE_INTEGER;
    const current = Number(this.quantityInput.value) || minimum;
    this.quantityInput.value = Math.min(maximum, Math.max(minimum, current + change));
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  setBusy(busy) {
    this.submitButtons.forEach((button) => {
      button.setAttribute('aria-busy', String(busy));
      if (busy) button.disabled = true;
      else button.disabled = !this.currentVariant?.available;
    });
  }

  async submit(event) {
    event.preventDefault();
    if (!this.currentVariant?.available) return;
    this.setBusy(true);
    this.status.textContent = 'Adding to your bag…';
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: new FormData(this.form)
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.description || 'Unable to add this expression.');
      }
      const item = await response.json();
      this.status.textContent = `${item.product_title} has been added to your bag.`;
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      this.status.textContent = error.message;
    } finally {
      this.setBusy(false);
    }
  }
}

class GiftPackGallery extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialised === 'true') return;
    this.dataset.initialised = 'true';
    this.tabs = [...this.querySelectorAll('[data-gift-tab]')];
    this.panels = [...this.querySelectorAll('[data-gift-panel]')];
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.tabs.length - 1;
        this.select(this.tabs[nextIndex]);
        this.tabs[nextIndex].focus();
      });
    });
  }

  select(tab) {
    const target = tab.dataset.giftTab;
    this.tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
    });
    this.panels.forEach((panel) => { panel.hidden = panel.dataset.giftPanel !== target; });
  }
}

customElements.define('rey-menu', ReyMenu);
customElements.define('age-gate', AgeGate);
customElements.define('product-gallery', ProductGallery);
customElements.define('product-form', ProductForm);
customElements.define('gift-pack-gallery', GiftPackGallery);

async function refreshCartCount() {
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('.cart-count').forEach((count) => { count.textContent = cart.item_count; });
    document.querySelectorAll('.cart-link').forEach((link) => link.setAttribute('aria-label', `Cart with ${cart.item_count} items`));
  } catch (error) { /* The product status already confirms the add. */ }
}

document.addEventListener('cart:updated', refreshCartCount);


if ('IntersectionObserver' in window) {
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });

  document.querySelectorAll('.reveal').forEach((element) => revealObserver.observe(element));
} else {
  document.querySelectorAll('.reveal').forEach((element) => element.classList.add('is-visible'));
}
